/**
 * ProgramPreview — always-visible mini thumbnail of the OBS PROGRAM
 * output, embedded in the top-right of the Presenter Console. Polls
 * `/api/obs/connections/{id}/screenshot` every N seconds. When the
 * connection is offline the preview shows a subtle disconnected chip
 * instead of throwing.
 *
 * Click the thumbnail to enlarge into a floating picture-in-picture
 * window; click again to shrink.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { RadioTower, Maximize2, Minimize2 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

export default function ProgramPreview({ connectionId, refreshMs = 1500 }) {
  const [dataUrl, setDataUrl] = useState('');
  const [scene, setScene] = useState('');
  const [error, setError] = useState('');
  const [pinned, setPinned] = useState(false);
  const [rate, setRate] = useState(refreshMs);
  const timerRef = useRef(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    let inflight = false;
    const tick = async () => {
      if (!connectionId || inflight || cancelRef.current) return;
      inflight = true;
      try {
        const r = await axios.get(
          `${API}/api/obs/connections/${connectionId}/screenshot?width=${pinned ? 640 : 360}&fmt=jpg`,
          { headers: authH(), timeout: 4000 },
        );
        if (cancelRef.current) return;
        if (r.data?.success && r.data.image_data_url) {
          setDataUrl(r.data.image_data_url);
          setScene(r.data.source_name || '');
          setError('');
        } else {
          setError(r.data?.error || 'unavailable');
        }
      } catch (e) {
        if (!cancelRef.current) setError(e?.message || 'offline');
      } finally {
        inflight = false;
      }
    };
    tick();
    timerRef.current = setInterval(tick, rate);
    return () => {
      cancelRef.current = true;
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [connectionId, rate, pinned]);

  if (!connectionId) return null;

  const size = pinned
    ? { width: 480, height: 270 }
    : { width: 200, height: 112 };

  return (
    <div
      data-testid="obs-program-preview"
      className={`fixed z-30 overflow-hidden rounded-lg border border-slate-700 bg-black shadow-2xl transition-all ${
        pinned ? 'top-4 right-4' : 'top-14 right-2'
      }`}
      style={size}
    >
      {dataUrl ? (
        <img src={dataUrl} alt="OBS program" className="h-full w-full object-cover" data-testid="obs-program-image" />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-500">
          <RadioTower className="h-4 w-4" />
          <span className="text-[9px]">{error || 'connecting…'}</span>
        </div>
      )}
      <div className="absolute top-1 left-1 flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] text-slate-100 uppercase tracking-wider">
        <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
        LIVE {scene && <span className="opacity-70 normal-case">· {scene}</span>}
      </div>
      <div className="absolute top-1 right-1 flex items-center gap-1">
        <select
          value={rate}
          onChange={(e) => setRate(Number(e.target.value))}
          onClick={(e) => e.stopPropagation()}
          data-testid="obs-preview-rate"
          className="rounded bg-black/70 px-1 py-0.5 text-[9px] text-slate-100"
        >
          <option value={1500}>1.5s</option>
          <option value={3000}>3s</option>
        </select>
        <button
          type="button"
          data-testid="obs-preview-toggle"
          onClick={() => setPinned((v) => !v)}
          className="rounded bg-black/70 p-0.5 text-slate-100 hover:bg-black/90"
          title={pinned ? 'Shrink' : 'Enlarge'}
        >
          {pinned ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}
